/**
 * Browery theme behaviour: scroll-snap slider, scroll reveal and the shipping countdown.
 * Kept dependency free and progressive: without JS the slider still scrolls natively.
 */

class BrowerySlider extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.browery-slider__track');
    this.slides = [...this.querySelectorAll('.browery-slider__slide')];
    if (!this.track || this.slides.length < 2) return;

    this.dots = [...this.querySelectorAll('.browery-slider__dot')];
    this.querySelector('.browery-slider__arrow--prev')?.addEventListener('click', () => this.go(this.index - 1));
    this.querySelector('.browery-slider__arrow--next')?.addEventListener('click', () => this.go(this.index + 1));
    this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.go(i)));

    this.index = 0;
    this.track.addEventListener('scroll', () => this.onScroll(), { passive: true });

    const autoplay = Number(this.dataset.autoplay || 0);
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (autoplay > 0 && !reducedMotion) {
      this.timer = setInterval(() => {
        if (!this.matches(':hover') && !this.contains(document.activeElement)) this.go(this.index + 1);
      }, autoplay);
    }
  }

  disconnectedCallback() {
    clearInterval(this.timer);
  }

  go(target) {
    const count = this.slides.length;
    const index = (target + count) % count;
    this.track.scrollTo({ left: this.slides[index].offsetLeft - this.track.offsetLeft, behavior: 'smooth' });
  }

  onScroll() {
    const index = Math.round(this.track.scrollLeft / this.track.clientWidth);
    if (index === this.index) return;
    this.index = index;
    this.dots.forEach((dot, i) => dot.setAttribute('aria-current', String(i === index)));
  }
}

if (!customElements.get('browery-slider')) customElements.define('browery-slider', BrowerySlider);

class BroweryCountdown extends HTMLElement {
  connectedCallback() {
    this.output = this.querySelector('[data-countdown]');
    this.cutoff = Number(this.dataset.cutoffHour || 19);
    this.tick();
    this.timer = setInterval(() => this.tick(), 1000);
  }

  disconnectedCallback() {
    clearInterval(this.timer);
  }

  tick() {
    const now = new Date();
    const cutoff = new Date(now);
    cutoff.setHours(this.cutoff, 0, 0, 0);
    if (now >= cutoff) cutoff.setDate(cutoff.getDate() + 1);

    const diff = Math.floor((cutoff - now) / 1000);
    const h = Math.floor(diff / 3600);
    const m = Math.floor((diff % 3600) / 60);
    const s = diff % 60;
    if (this.output) this.output.textContent = `${h}h ${String(m).padStart(2, '0')}m ${String(s).padStart(2, '0')}s`;
  }
}

if (!customElements.get('browery-countdown')) customElements.define('browery-countdown', BroweryCountdown);

function initReveal(root = document) {
  const items = root.querySelectorAll('.browery-reveal:not(.is-visible)');
  if (!('IntersectionObserver' in window)) {
    items.forEach((el) => el.classList.add('is-visible'));
    return;
  }
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      });
    },
    { rootMargin: '0px 0px -10% 0px' }
  );
  items.forEach((el) => observer.observe(el));
}

initReveal();
document.addEventListener('shopify:section:load', (event) => initReveal(event.target));

// Highlight today's row in store opening hours ("Montag 10:00 Uhr bis 19:00 Uhr").
function highlightOpeningHours(root = document) {
  const today = ['sonntag', 'montag', 'dienstag', 'mittwoch', 'donnerstag', 'freitag', 'samstag'][new Date().getDay()];
  root.querySelectorAll('[data-browery-hours] li').forEach((row) => {
    row.classList.toggle('is-today', row.dataset.day.replace(':', '') === today);
  });
}

highlightOpeningHours();
document.addEventListener('shopify:section:load', (event) => highlightOpeningHours(event.target));

// Store cards: show today's opening hours ("Montag 10:00 Uhr bis 20:00 Uhr" lines, joined with "|").
function showTodayHours(root = document) {
  const days = ['sonntag', 'montag', 'dienstag', 'mittwoch', 'donnerstag', 'freitag', 'samstag'];
  const today = days[new Date().getDay()];
  root.querySelectorAll('[data-browery-today-hours]').forEach((el) => {
    const line = el.dataset.broweryTodayHours
      .split('|')
      .map((entry) => entry.trim())
      .find((entry) => entry.toLowerCase().replace(':', '').startsWith(today));
    const hours = line ? line.slice(line.indexOf(' ') + 1).replace(/ Uhr bis /g, '–').trim() : '';
    el.classList.toggle('is-open', Boolean(hours));
    el.textContent = hours ? `Heute geöffnet · ${hours}` : 'Heute geschlossen';
  });
}

showTodayHours();
document.addEventListener('shopify:section:load', (event) => showTodayHours(event.target));

// Store overview: city filter chips.
document.addEventListener('click', (event) => {
  const chip = event.target.closest('[data-browery-city-filter] [data-city]');
  if (!chip) return;
  const group = chip.closest('[data-browery-city-filter]');
  group.querySelectorAll('[data-city]').forEach((el) => el.setAttribute('aria-pressed', String(el === chip)));
  const section = group.closest('section');
  section.querySelectorAll('.browery-store-card').forEach((card) => {
    card.hidden = Boolean(chip.dataset.city) && card.dataset.city !== chip.dataset.city;
  });
});

// Store overview: "Auf Karte zeigen" zooms the shared map to the store.
document.addEventListener('click', (event) => {
  const button = event.target.closest('.browery-stores__show');
  if (!button) return;
  const map = document.querySelector('[data-browery-stores-map]');
  if (!map) return;
  map.src = `https://maps.google.com/maps?q=${button.dataset.lat},${button.dataset.lng}&z=16&hl=de&output=embed`;
  map.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
});

// Timed popup (sections/browery-popup.liquid). Dismissal is remembered per campaign in localStorage.
// In newsletter mode the form subscribes via Mailchimp's JSONP endpoint, then reveals the discount code.
class BroweryPopup extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    if (!this.dialog) return;

    this.querySelectorAll('[data-popup-close]').forEach((el) => el.addEventListener('click', () => this.close()));
    this.dialog.addEventListener('cancel', () => this.snooze());
    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.close();
    });
    this.querySelector('[data-popup-form]')?.addEventListener('submit', (event) => this.subscribe(event));
    this.querySelector('[data-popup-copy]')?.addEventListener('click', (event) => this.copyCode(event.currentTarget));

    if (this.hasAttribute('data-design-mode')) return;
    // Visitors arriving from a newsletter are already subscribed.
    if (this.hasAttribute('data-newsletter') && /utm_medium=email/i.test(location.search)) return this.snooze(24 * 365);
    if (this.isSnoozed()) return;
    this.scheduleOpen();
  }

  // Desktop: after the delay or on exit intent. Mobile: only after a longer delay and once the visitor
  // has scrolled a third of the page, so the popup never blocks the first impression (Google interstitial rules).
  scheduleOpen() {
    const delay = Number(this.dataset.delay || 0);
    const isMobile = window.matchMedia('(max-width: 749px)').matches;

    if (!isMobile) {
      this.timer = setTimeout(() => this.open(), delay);
      this.onExitIntent = (event) => {
        if (event.clientY <= 0 && !event.relatedTarget) this.open();
      };
      document.documentElement.addEventListener('mouseleave', this.onExitIntent);
      return;
    }

    let waited = false;
    let scrolled = false;
    const tryOpen = () => waited && scrolled && this.open();
    this.timer = setTimeout(() => {
      waited = true;
      tryOpen();
    }, Math.max(delay, 20000));
    this.onScroll = () => {
      const depth = (window.scrollY + window.innerHeight) / document.documentElement.scrollHeight;
      if (depth < 0.33) return;
      scrolled = true;
      window.removeEventListener('scroll', this.onScroll);
      tryOpen();
    };
    window.addEventListener('scroll', this.onScroll, { passive: true });
  }

  disconnectedCallback() {
    clearTimeout(this.timer);
    window.removeEventListener('scroll', this.onScroll);
    document.documentElement.removeEventListener('mouseleave', this.onExitIntent);
  }

  open() {
    if (this.opened || this.isSnoozed()) return;
    if (document.querySelector('dialog[open]')) return; // never stack on top of the cart or menu drawer
    this.opened = true;
    clearTimeout(this.timer);
    document.documentElement.removeEventListener('mouseleave', this.onExitIntent);
    this.dialog.showModal();
  }

  close() {
    if (!this.subscribed) this.snooze();
    this.dialog.close();
  }

  isSnoozed() {
    try {
      return Number(localStorage.getItem(this.dataset.key) || 0) > Date.now();
    } catch (error) {
      return false;
    }
  }

  snooze(hours = Number(this.dataset.snoozeHours || 24)) {
    try {
      localStorage.setItem(this.dataset.key, String(Date.now() + hours * 3600 * 1000));
    } catch (error) {
      // Storage unavailable (private mode): the popup simply shows again next visit.
    }
  }

  async subscribe(event) {
    event.preventDefault();
    const form = event.currentTarget;
    const email = form.elements.EMAIL;
    const button = form.querySelector('[type="submit"]');
    const error = form.querySelector('[data-popup-error]');

    error.hidden = true;
    if (!email.checkValidity()) {
      this.showError(error, 'Bitte gib eine gültige E-Mail-Adresse ein.');
      email.focus();
      return;
    }

    button.disabled = true;
    try {
      const result = await this.mailchimp(form);
      if (result.result === 'success' || /already subscribed|bereits/i.test(result.msg || '')) {
        this.subscribed = true;
        this.snooze(24 * 365);
        this.querySelector('[data-popup-step="intro"]').hidden = true;
        this.querySelector('[data-popup-step="success"]').hidden = false;
        this.querySelector('[data-popup-copy]')?.focus();
      } else {
        this.showError(error, 'Das hat leider nicht geklappt. Bitte prüfe deine E-Mail-Adresse.');
      }
    } catch (e) {
      this.showError(error, 'Gerade gibt es ein Verbindungsproblem. Bitte versuch es gleich noch einmal.');
    } finally {
      button.disabled = false;
    }
  }

  showError(element, message) {
    element.textContent = message;
    element.hidden = false;
  }

  // Mailchimp has no CORS endpoint for hosted forms; post-json with a callback is their JSONP variant.
  mailchimp(form) {
    return new Promise((resolve, reject) => {
      const callback = `broweryMailchimp${Date.now()}`;
      const url = new URL(form.action.replace('/post?', '/post-json?'));
      new FormData(form).forEach((value, key) => url.searchParams.set(key, value));
      url.searchParams.set('c', callback);

      const script = document.createElement('script');
      const cleanup = () => {
        delete window[callback];
        script.remove();
        clearTimeout(timeout);
      };
      const timeout = setTimeout(() => {
        cleanup();
        reject(new Error('timeout'));
      }, 10000);
      window[callback] = (data) => {
        cleanup();
        resolve(data);
      };
      script.onerror = () => {
        cleanup();
        reject(new Error('network'));
      };
      script.src = url.toString();
      document.head.append(script);
    });
  }

  async copyCode(button) {
    const label = button.querySelector('[data-popup-copy-label]');
    try {
      await navigator.clipboard.writeText(button.dataset.popupCopy);
      label.textContent = 'Kopiert';
    } catch (error) {
      label.textContent = 'Code markieren und kopieren';
    }
  }
}

if (!customElements.get('browery-popup')) customElements.define('browery-popup', BroweryPopup);

// Theme editor: show the popup while its section is selected.
document.addEventListener('shopify:section:select', (event) => {
  event.target.querySelector('browery-popup dialog:not([open])')?.showModal();
});
document.addEventListener('shopify:section:deselect', (event) => {
  event.target.querySelector('browery-popup dialog[open]')?.close();
});
